/**
 * GridBufferNode — feedback latch for grid simulations.
 *
 * Endpoints:
 *   tick  (trigger, isInput)  — advance one step
 *   init  (internal, isInput) — initial GridState from GridInit
 *   in    (internal, isInput) — result from end of system chain
 *   out   (internal, isOutput) — pushes state to system chain on tick
 *   current (internal, isOutput, pushToStore) — current state for GridViz
 *
 * On tick: push stored state to `out` (synchronous chain executes,
 * result arrives at `in` within the same call), then push to `current`.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@/graph/Payload';
import { triggers, internals } from '@/nodes/TypedEndpoints';
import type { GridState } from '../GridState';
import { logger } from '@/logging';

const endpoints = {
  ...triggers({
    tick: { isInput: true, label: 'Tick' },
  }),
  ...internals({
    init:    { dataType: 'any' as any, isInput: true, label: 'Init' },
    in:      { dataType: 'any' as any, isInput: true, label: 'In' },
    out:     { dataType: 'any' as any, isOutput: true, label: 'Out' },
    current: { dataType: 'any' as any, isOutput: true, label: 'Current', pushToStore: true },
  }),
};

class GridBufferNode extends BaseNode<typeof endpoints> {
  private storedState: GridState | null = null;
  private propagating = false;

  onInternalChanged(payload: Payload, endpointId: string): void {
    const state = (payload as any)?.value ?? payload;
    logger.trace('GridBuffer', `onInternalChanged '${endpointId}': hasLayers=${'layers' in (state ?? {})}`);

    if (endpointId === 'init' && state && typeof state === 'object' && 'layers' in state) {
      this.storedState = state as GridState;
      logger.trace('GridBuffer', `init received, pushing to current. layers=${Object.keys((state as any)?.layers ?? {}).join(',')}`);
      this.internals.current.set(this.storedState);
    } else if (endpointId === 'in') {
      // Result from system chain — store but do NOT push to `out` (prevents loop)
      // Ignore non-GridState values (e.g. [] default from output endpoint propagation)
      if (state && typeof state === 'object' && 'layers' in state) {
        this.storedState = state as GridState;
      }
    }
  }

  handleTrigger(_payload: Payload, endpointId: string): void {
    if (endpointId === 'tick' && this.storedState && !this.propagating) {
      this.propagating = true;
      // Push to out — the synchronous chain runs and `in` receives the result
      this.internals.out.set(this.storedState);
      this.propagating = false;
      // Now push updated state to current (for visualization)
      this.internals.current.set(this.storedState);
    }
  }
}

export const gridBufferNodeDef: NodeDef<GridBufferNode> = {
  type: 'GridBuffer',
  shortName: 'gridbuf',
  label: 'Grid Buffer',
  info: 'Feedback latch for grid simulations. Connect init from GridInit, out to system chain, in from chain end, current to GridViz.',
  domain: Domain.Data,
  endpoints,
  ctor: GridBufferNode,
};
